import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  SelectChangeEvent,
} from '@mui/material';
import axios from 'axios';
import SidebarLayout from '../components/SidebarLayout';

interface Record {
  id: number;
  user_id: number;
  total: number;
  start_time: string;
  end_time: string;
  created_at: string;
  type: number;
  user: {
    username: string;
  };
}

const UseRecord = () => {
  const [records, setRecords] = useState<Record[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [type, setType] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [users, setUsers] = useState<{ id: number; username: string }[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchRecords();
    checkUserRole();
    fetchUsers();
  }, [page, type, userId]);

  const checkUserRole = async () => {
    try {
      const response = await axios.get('/api/users/me');
      setIsAdmin(response.data.role === 'admin');
    } catch (error) {
      console.error('获取用户角色失败:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data.users);
    } catch (error) {
      console.error('获取用户列表失败:', error);
    }
  };

  const fetchRecords = async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (type) params.append('type', type);
      if (userId) params.append('userId', userId);

      const response = await axios.get(`/api/use-records?${params}`);
      setRecords(response.data.records);
      setTotal(response.data.total);
    } catch (error) {
      console.error('获取记录失败:', error);
    }
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setType(event.target.value);
    setPage(1);
  };

  const handleUserChange = (event: SelectChangeEvent) => {
    setUserId(event.target.value);
    setPage(1);
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '-';
    const date = new Date(timeStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <SidebarLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4">操作记录</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {isAdmin && (
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel>用户</InputLabel>
                <Select
                  value={userId}
                  label="用户"
                  onChange={handleUserChange}
                >
                  <MenuItem value="">全部</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id.toString()}>
                      {user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>类型</InputLabel>
              <Select
                value={type}
                label="类型"
                onChange={handleTypeChange}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="1">Bili 弹幕</MenuItem>
                <MenuItem value="2">Bili 评论</MenuItem>
                <MenuItem value="3">QQ音乐评论</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>开始时间</TableCell>
                <TableCell>结束时间</TableCell>
                <TableCell>用户名称</TableCell>
                <TableCell>发送总数</TableCell>
                <TableCell>类型</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{formatTime(record.start_time)}</TableCell>
                  <TableCell>{formatTime(record.end_time)}</TableCell>
                  <TableCell>{record.user?.username}</TableCell>
                  <TableCell>{record.total}</TableCell>
                  <TableCell>
                    {record.type === 1 ? 'Bili 弹幕' : record.type === 2 ? 'Bili 评论' : record.type === 3 ? 'QQ音乐评论' : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
          <Pagination
            count={Math.ceil(total / limit)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            总条数: {total}
          </Typography>
        </Box>
      </Container>
    </SidebarLayout>
  );
};

export default UseRecord;